/**
 * mpClient — Phase 5 (P3-41/42) protocol-aware WS client + P6-11 browser API.
 * Transport injected via openSocket(url) → socket with send / addEventListener / close.
 */

import {
  WS_MSG,
  createEnvelope,
  encodeMessage,
  decodeMessage
} from '../../domain/net/wsProtocol.js';
import {
  handleBrowserServerMsg,
  listRooms as browserListRooms,
  createRoom as browserCreateRoom,
  fillBots as browserFillBots,
  startMatch as browserStartMatch,
  setTeam as browserSetTeam,
  rejectBrowserWaits
} from './mpClientBrowser.js';

function createState(options) {
  return {
    options,
    now: options.now || Date.now,
    welcomeTimeoutMs: options.welcomeTimeoutMs ?? 3000,
    matchStartTimeoutMs: options.matchStartTimeoutMs ?? 600000,
    socket: null,
    status: 'idle',
    clientId: null,
    seq: 0,
    welcomeWait: null,
    pingWait: null,
    roomWait: null,
    listWait: null,
    matchWait: null,
    roomState: null,
    roomListeners: [],
    snapshot: null,
    snapshotListeners: [],
    ackListeners: [],
    matchStart: null
  };
}

function send(state, type, payload) {
  if (!state.socket || state.socket.readyState !== 1) return;
  state.seq += 1;
  const env = createEnvelope(type, payload, state.seq);
  state.socket.send(encodeMessage(env));
}

function resolveWait(wait, value) {
  if (!wait) return;
  if (wait.timer) clearTimeout(wait.timer);
  wait.resolve(value);
}

function onMessage(state, event) {
  const text = typeof event.data === 'string' ? event.data : '';
  const decoded = decodeMessage(text);
  if (!decoded.ok) return;
  const env = decoded.envelope;
  if (handleBrowserServerMsg(state, env)) return;
  if (env.type === WS_MSG.WELCOME) {
    state.clientId = env.payload.clientId;
    state.status = 'welcomed';
    if (state.welcomeWait) {
      state.welcomeWait.resolve(env);
      state.welcomeWait = null;
    }
    return;
  }
  if (env.type === WS_MSG.PONG && state.pingWait) {
    state.pingWait.resolve(env);
    state.pingWait = null;
    return;
  }
  if (env.type === WS_MSG.ROOM_STATE) {
    state.roomState = env.payload;
    for (const fn of state.roomListeners) fn(env.payload);
    if (state.roomWait) {
      resolveWait(state.roomWait, env.payload);
      state.roomWait = null;
    }
    return;
  }
  if (env.type === WS_MSG.MATCH_START) {
    state.status = 'match_starting';
    state.matchStart = env.payload;
    if (state.matchWait) {
      resolveWait(state.matchWait, env.payload);
      state.matchWait = null;
    }
    return;
  }
  if (env.type === WS_MSG.CMD_ACK) {
    for (const fn of state.ackListeners) fn(env.payload);
    return;
  }
  if (env.type === WS_MSG.STATE_SNAPSHOT) {
    state.snapshot = env.payload;
    for (const fn of state.snapshotListeners) fn(env.payload);
  }
}

function connect(state) {
  if (state.status !== 'idle' && state.status !== 'closed') {
    return Promise.reject(new Error('already_connected'));
  }
  state.status = 'connecting';
  state.socket = state.options.openSocket(state.options.url);
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      state.status = 'error';
      state.welcomeWait = null;
      reject(new Error('welcome_timeout'));
    }, state.welcomeTimeoutMs);
    state.welcomeWait = {
      resolve(env) {
        clearTimeout(timer);
        resolve(env);
      }
    };
    state.socket.addEventListener('message', (e) => onMessage(state, e));
    state.socket.addEventListener('open', () => {
      state.status = 'open';
      send(state, WS_MSG.HELLO, {
        displayName: state.options.displayName || 'Player'
      });
    });
  });
}

function requestPing(state) {
  if (state.status !== 'welcomed' && state.status !== 'match_starting') {
    return Promise.reject(new Error('not_welcomed'));
  }
  return new Promise((resolve, reject) => {
    state.pingWait = { resolve, reject };
    send(state, WS_MSG.PING, { t: state.now() });
  });
}

function joinRoom(state, payload = {}) {
  if (state.status !== 'welcomed' && state.status !== 'match_starting') {
    return Promise.reject(new Error('not_welcomed'));
  }
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      state.roomWait = null;
      reject(new Error('room_state_timeout'));
    }, state.welcomeTimeoutMs);
    state.roomWait = {
      resolve(room) {
        clearTimeout(timer);
        resolve(room);
      },
      reject,
      timer
    };
    send(state, WS_MSG.JOIN_ROOM, payload);
  });
}

function lockIn(state, loadout) {
  send(state, WS_MSG.LOCK_IN, loadout || {});
}

function waitForMatchStart(state) {
  if (state.matchStart) {
    return Promise.resolve(state.matchStart);
  }
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      state.matchWait = null;
      reject(new Error('match_start_timeout'));
    }, state.matchStartTimeoutMs);
    state.matchWait = {
      resolve(match) {
        clearTimeout(timer);
        resolve(match);
      },
      reject,
      timer
    };
  });
}

function sendMove(state, x, z) {
  state.seq += 1;
  const seq = state.seq;
  const env = createEnvelope(WS_MSG.CMD_MOVE, { x, z }, seq);
  if (state.socket && state.socket.readyState === 1) {
    state.socket.send(encodeMessage(env));
  }
  return seq;
}

function close(state) {
  if (state.socket) state.socket.close();
  state.socket = null;
  state.status = 'closed';
  state.welcomeWait = null;
  state.pingWait = null;
  rejectBrowserWaits(state, 'closed');
}

/**
 * @param {{
 *   openSocket: (url?: string) => object,
 *   url?: string,
 *   displayName?: string,
 *   welcomeTimeoutMs?: number,
 *   now?: () => number
 * }} options
 */
export function createMpClient(options) {
  const state = createState(options);
  return {
    connect: () => connect(state),
    requestPing: () => requestPing(state),
    joinRoom: (payload) => joinRoom(state, payload),
    createRoom: (payload) => browserCreateRoom(state, payload),
    listRooms: () => browserListRooms(state),
    fillBots: (roomId, targetSize) => browserFillBots(state, roomId, targetSize),
    setTeam: (roomId, team) => browserSetTeam(state, roomId, team),
    assignTeam: (roomId, clientId, team) =>
      browserSetTeam(state, roomId, team, clientId),
    startMatch: (roomId) => browserStartMatch(state, roomId),
    lockIn: (loadout) => lockIn(state, loadout),
    waitForMatchStart: () => waitForMatchStart(state),
    sendMove: (x, z) => sendMove(state, x, z),
    onRoomState: (fn) => {
      state.roomListeners.push(fn);
    },
    onSnapshot: (fn) => {
      state.snapshotListeners.push(fn);
    },
    onCmdAck: (fn) => {
      state.ackListeners.push(fn);
    },
    getRoomState: () => state.roomState,
    getSnapshot: () => state.snapshot,
    getMatchStart: () => state.matchStart || null,
    close: () => close(state),
    getStatus: () => state.status,
    getClientId: () => state.clientId,
    send: (type, payload) => send(state, type, payload)
  };
}
